import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuditorVisitDetail } from '../types';

type AuditorVisitDetailContextType = {
  auditorVisit: AuditorVisitDetail;
  invalidateAuditorVisit: () => void;
};

const AuditorVisitDetailContext = createContext<AuditorVisitDetailContextType | null>(null);

const useAuditorVisitDetailContext = () => {
  const auditorVisitDetailContext = useContext(AuditorVisitDetailContext);

  if (!auditorVisitDetailContext) {
    throw new Error('auditorVisitDetailContext must be used within AuditorVisitDetailProvider.');
  }

  return auditorVisitDetailContext;
};

type AuditorVisitDetailProviderProps = {
  auditorVisit: AuditorVisitDetail;
} & PropsWithChildren;

const AuditorVisitDetailProvider = ({
  auditorVisit,
  children,
}: AuditorVisitDetailProviderProps) => {
  const queryClient = useQueryClient();

  const invalidateAuditorVisit = () => {
    queryClient.invalidateQueries(['auditor-visits-id', auditorVisit.id]);
  };

  const value = useMemo<AuditorVisitDetailContextType>(
    () => ({
      auditorVisit,
      invalidateAuditorVisit,
    }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auditorVisit]
  );

  return (
    <AuditorVisitDetailContext.Provider value={value}>
      {children}
    </AuditorVisitDetailContext.Provider>
  );
};

export { AuditorVisitDetailProvider, useAuditorVisitDetailContext };
